import CachedIcon from '@mui/icons-material/Cached';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { IconButton, styled, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { autobind } from 'core-decorators';
import isEmpty from 'lodash/isEmpty';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Component } from 'react';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Recommendation } from '/@/types/recommendation';
import { isMobile, openWhatsappWeb, validateEmailV2 } from '/@/utils';
import ModalV2 from '../bayer/modal-v2';
import { ShareItemPopup, type ShareItemPopupMode } from '../share-item-popup';

@autobind
class SharePopupClass extends Component {
    static propTypes = {
        visible: PropTypes.bool.isRequired,
        title: PropTypes.string.isRequired,
        whatsAppIntro: PropTypes.string,
        onEmail: PropTypes.func,
        onWhatsApp: PropTypes.func,
        onDownload: PropTypes.func,
        onClose: PropTypes.func.isRequired,
        recommendation: PropTypes.object,
        type: PropTypes.string,
        version: PropTypes.number,
        text: PropTypes.string,
    };

    static defaultProps = {
        version: 1,
        text: _(
            'Elegí cómo querés recibir tu recomendación. Podés enviarla por WhatsApp, mail o descargarla en formato PDF.'
        ),
    };

    initialState = {
        view: 'buttons',
        file: undefined,
        email: '',
        emailSuccess: false,
        crop: undefined,
        source: undefined,
        numberOfHybrids: undefined,
    };

    state = this.initialState;

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!nextProps.visible) {
            this.setState(this.initialState);
        }
    }

    render() {
        const { visible, onClose } = this.props;
        const { view } = this.state;

        if (view === 'buttons') return this.renderButtons();
        if (view === 'download') return this.renderDownload();
        if (view === 'email') return this.renderEmail();

        return null;
    }

    renderButtons() {
        const { title, onDownload, onEmail, onWhatsApp, onClose, visible } = this.props;

        return (
            <ModalV2
                open={visible}
                title={title}
                onClose={onClose}
                actionsProps={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
                actions={
                    <>
                        {onEmail && (
                            <IconButton onClick={this.handleEmail}>
                                <EmailIcon color='secondary' sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                        {onWhatsApp && (
                            <IconButton onClick={this.handleWhatsApp}>
                                <WhatsAppIcon color='secondary' sx={{ fontSize: 20 }} />
                            </IconButton>
                        )}
                        {onDownload && (
                            <Button
                                variant='contained'
                                startIcon={<DownloadIcon sx={{ fontSize: 20 }} />}
                                onClick={this.handleDownload}
                                sx={{ mt: 1 }}
                                color='secondary'
                            >
                                {_('DESCARGAR')}
                            </Button>
                        )}
                    </>
                }
            >
                <Typography variant='subtitle1' color='primary'>
                    {this.props.text}
                </Typography>
            </ModalV2>
        );
    }

    renderDownload() {
        const { file } = this.state;
        const { visible, onClose } = this.props;

        return (
            <ModalV2
                open={visible}
                onClose={onClose}
                title={_('Descargar PDF')}
                actions={
                    <Button
                        startIcon={file ? <DownloadIcon /> : <SpinningCachedIcon />}
                        disabled={!file}
                        onClick={async () => {
                            if (file) {
                                // Don't await to update optimistically, email is sent via offline sync mechanism.
                                const { crop, source, numberOfHybrids } = this.state;
                                const { recommendation, type } = this.props;
                                if (type === 'crop-protection') {
                                    mpTrackEvent('Crop Protection Recommendation downloaded', {
                                        entry_source: 'my-recommendations',
                                        crop: recommendation.type,
                                        cp_type: recommendation.typeLabel,
                                    });
                                } else if (['corn', 'soy', 'weed'].includes(type)) {
                                    mpTrackEvent('Seed recommendation downloaded', {
                                        crop,
                                        number_of_hybrids: numberOfHybrids,
                                        entry_source: source,
                                        recommendation_type: type === 'weed' ? undefined : 'hybrid',
                                    });
                                }
                            }
                            window.open(file.url);
                        }}
                    >
                        {file ? _('Descargar PDF') : _('Generando PDF...')}
                    </Button>
                }
                actionsProps={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <Typography variant='subtitle1' color='primary'>
                    {!file
                        ? _('Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.')
                        : _('¡Tu recomendación está lista!')}
                </Typography>
            </ModalV2>
        );
    }

    renderEmail() {
        const { emailSuccess } = this.state;
        const { visible, onClose } = this.props;

        if (emailSuccess)
            return (
                <ModalV2 open={visible} onClose={onClose} title={_('Enviar recomendación')}>
                    <Typography variant='h6' sx={{ pt: 2 }} color='primary'>
                        {_('¡Recomendación enviada con éxito!')}
                    </Typography>
                </ModalV2>
            );

        return (
            <ModalV2
                open={visible}
                onClose={onClose}
                goBack={() => this.setState({ view: 'buttons' })}
                title={_('Enviar recomendación')}
                actionsProps={{
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
                actions={
                    <Button
                        color='secondary'
                        variant='contained'
                        type='submit'
                        disabled={!validateEmailV2(this.state.email)}
                        sx={{ ml: 1, mt: 1 }}
                        onClick={this.handleEmailSend}
                    >
                        {_('ENVIAR')}
                    </Button>
                }
            >
                <Typography variant='subtitle1' color='primary'>
                    {_('Ingresá la dirección de correo electrónico del destinatario.')}
                </Typography>

                <TextField
                    value={this.state.email}
                    onChange={(e) => {
                        this.setState({ email: e.target.value });
                    }}
                    label={_('Email')}
                    variant='outlined'
                    size='small'
                    fullWidth
                />
            </ModalV2>
        );
    }

    handleEmail() {
        this.setState({ view: 'email' });
    }

    async handleEmailSend(event) {
        event.preventDefault();

        // Don't await to update optimistically, email is sent via offline sync mechanism.
        const { crop, source, numberOfHybrids } = await this.props.onEmail({ email: this.state.email });
        const { type, recommendation } = this.props;
        if (type === 'crop-protection') {
            mpTrackEvent('Crop Protection Recommendation shared', {
                entry_source: 'my-recommendations',
                crop: recommendation?.type,
                cp_type: recommendation?.typeLabel,
                share_method: 'email',
            });
        } else if (['corn', 'soy', 'weed'].includes(type)) {
            mpTrackEvent('Seed recommendation shared', {
                share_method: 'email',
                crop,
                number_of_hybrids: numberOfHybrids,
                entry_source: source,
                recommendation_type: type === 'weed' ? undefined : 'hybrid',
            });
        }

        this.setState({ emailSuccess: true });
    }

    async handleWhatsApp() {
        const { file, crop, source, numberOfHybrids } = await this.props.onWhatsApp();
        const { recommendation, type } = this.props;

        if (type === 'crop-protection') {
            mpTrackEvent('Crop Protection Recommendation shared', {
                entry_source: 'my-recommendations',
                crop: recommendation?.type,
                cp_type: recommendation?.typeLabel,
                share_method: 'whatsapp',
            });
        } else if (['corn', 'soy', 'weed'].includes(type)) {
            mpTrackEvent('Seed recommendation shared', {
                share_method: 'whatsapp',
                crop,
                number_of_hybrids: numberOfHybrids,
                entry_source: source,
                recommendation_type: type === 'weed' ? undefined : 'hybrid',
            });
        }

        const message = encodeURIComponent(`${this.props.whatsAppIntro}\n${settings.app.url + file.url}`);
        if (isMobile()) window.location.href = `whatsapp://send?text=${message}`;
        else openWhatsappWeb({ message: `${this.props.whatsAppIntro}\n${settings.app.url + file.url}` });
    }

    async handleDownload() {
        this.setState({ view: 'download' });
        try {
            const { file, crop, source, numberOfHybrids } = await this.props.onDownload();
            this.setState({ file, crop, source, numberOfHybrids });
        } catch (error) {
            enqueueSnackbar(_('Ha ocurrido un error, por favor inténtalo más tarde.'), {
                variant: 'error',
            });
            this.setState(this.initialState);
        }
    }
}

const SpinningCachedIcon = styled(CachedIcon)({
    animation: 'spin 1s linear infinite',
    '@keyframes spin': {
        '0%': {
            transform: 'rotate(0deg)',
        },
        '100%': {
            transform: 'rotate(360deg)',
        },
    },
});

interface SharePopupProps {
    visible: boolean;
    title: string;
    whatsAppIntro?: string;
    onEmail?: (args: {
        email: string;
    }) => Promise<{ file: any; crop?: string; source?: string; numberOfHybrids?: number }>;
    onWhatsApp?: () => Promise<{ file: any; crop?: string; source?: string; numberOfHybrids?: number }>;
    onDownload?: () => Promise<{ file: any; crop?: string; source?: string; numberOfHybrids?: number }>;
    onClose: () => void;
    recommendation?: Recommendation;
    type?: string;
    version?: number;
    isExpertoBayer?: boolean;
    entrySource?: string;
    /** PDF template to request; only version 2 offers block selection. */
    pdfVersion?: number;
    /** Opens the v2 popup on its block picker. Omit to keep the existing behaviour. */
    mode?: ShareItemPopupMode;
}

function SharePopup({
    visible,
    title,
    whatsAppIntro,
    onEmail,
    onWhatsApp,
    onDownload,
    onClose,
    recommendation,
    type,
    version,
    entrySource,
    pdfVersion,
    mode,
}: SharePopupProps) {
    if (version === 2 && !isEmpty(recommendation)) {
        return (
            <ShareItemPopup
                visible={visible}
                onClose={onClose}
                itemType='recommendation'
                item={recommendation}
                entrySource={entrySource}
                pdfVersion={pdfVersion}
                mode={mode}
            />
        );
    }
    return (
        <SharePopupClass
            visible={visible}
            title={title}
            whatsAppIntro={whatsAppIntro}
            onEmail={onEmail}
            onWhatsApp={onWhatsApp}
            onDownload={onDownload}
            onClose={onClose}
            recommendation={recommendation}
            type={type}
            version={version}
        />
    );
}

export default SharePopup;
